import { TabItemData } from "@bundles/misc/components/tab-control"
import { Icon, LegacyContainer, SimpleWidgetCard, SimpleWidgetCardProps, Stack, styled, Tab, TabPanel, Tabs } from "@tm/components"
import { useOffersModules } from "@tm/context-distribution"
import { useLocalization } from "@tm/localization"
import { useCallback, useEffect, useState } from "react"
import { classes, encodeUniqueId, renderRoute, uniqueId } from "@tm/utils"
import { useHistory, useParams } from "react-router"
import { useAdvertisementCategories } from "@tm/data/erpConnect"
import { SxProps, useThemeProps } from "@mui/material"
import { WidgetSizes } from "@tm/controls"
import { offersWidgetClasses, offersWidgetName, offersWidgetSlots } from "@tm/themes"
import { OffersFrame } from "./components/OffersFrame"
import { OffersCarousel } from "./components/Carousel"
import { useOffersStore } from "./store"
import { OfferDetailsButton } from "./components/OfferDetailsButton"
import { useDisabledFunction } from "../../hooks/useDisabledFuntion"
import { ConfigQueryParams } from "../shared"

export type OffersWidgetProps = {
    externalSystemTabs?: TabItemData[]
    showOfferCarouselAsChild?: boolean
    hideTitle?: boolean
    className?: string
    hideShowAllButton?: boolean
    disabledFunction?: string
    size?: WidgetSizes
    sx?: SxProps
    variant?: SimpleWidgetCardProps["widgetVariant"]
    iframeParams?: ConfigQueryParams
}

const OffersWidgetTabs = styled(Tabs, {
    name: offersWidgetName,
    slot: offersWidgetSlots.tabs,
    overridesResolver: (_, styles) => styles.tabs,
})({
    flex: "0 0 auto",
})

const OffersWidgetTabPanel = styled(TabPanel, {
    name: offersWidgetName,
    slot: offersWidgetSlots.tabPanel,
    overridesResolver: (_, styles) => styles.tabPanel,
})({
    paddingLeft: 0,
    display: "flex",
    flex: 1,
})

const OFFERS_KEY = "OFFERS"
export const OFFER_CAROUSEL_KEY = "OFFER_CAROUSEL"
const GENERATED_WORKTASK_ID = uniqueId()

function OffersWidgetComponent(props: OffersWidgetProps) {
    const {
        externalSystemTabs,
        showOfferCarouselAsChild,
        className,
        hideTitle,
        hideShowAllButton,
        disabledFunction,
        size,
        sx,
        variant,
        iframeParams,
    } = useThemeProps({
        props,
        name: offersWidgetName,
    })

    const { isTranslationId, translateText } = useLocalization()
    const history = useHistory()
    const params = useParams<{ workTaskId?: string }>()
    const { isDisabled } = useDisabledFunction({ disabledFunction })

    const { advertisementCategories } = useAdvertisementCategories(!showOfferCarouselAsChild)
    const { setCategories, setSelectedCategory, setOffersTabActive, setCurrentCarouselPageIndex } = useOffersStore()
    const { hasOfferCarouselModuleEnabled, hasOffersModuleEnabled, offerCarouselModuleDescription, offersModuleDescription } = useOffersModules()
    const [activeTabIdentifier, setActiveTabIdentifier] = useState(OFFERS_KEY)

    useEffect(() => {
        if (hasOffersModuleEnabled) {
            setActiveTabIdentifier(OFFERS_KEY)
            setOffersTabActive(true)
        } else if (hasOfferCarouselModuleEnabled) {
            setActiveTabIdentifier(OFFER_CAROUSEL_KEY)
        }
    }, [hasOfferCarouselModuleEnabled, hasOffersModuleEnabled])

    useEffect(() => {
        if (advertisementCategories) {
            setCategories(advertisementCategories)
            setSelectedCategory(advertisementCategories[0])
            if (!hasOffersModuleEnabled) {
                setActiveTabIdentifier(`${OFFER_CAROUSEL_KEY}|${advertisementCategories[0].id}`)
            }
        }
    }, [advertisementCategories, hasOffersModuleEnabled])

    const getTabText = useCallback(
        (text: string) => {
            return isTranslationId(text) ? translateText(text) : text
        },
        [isTranslationId, translateText]
    )

    const openLink = useCallback(
        (path: string) => {
            const url = renderRoute(path, { ...params, workTaskId: params.workTaskId ?? encodeUniqueId(GENERATED_WORKTASK_ID) })
            history.push(url)
        },
        [history, params]
    )

    const handleTabChange = useCallback(
        (_: unknown, value: string) => {
            const [key, catId] = value.split("|")
            if (key === OFFER_CAROUSEL_KEY) {
                const category = advertisementCategories?.find((x) => x.id === catId)
                if (category) {
                    setSelectedCategory(category)
                }
            }
            setOffersTabActive(value === OFFERS_KEY)
            setActiveTabIdentifier(value)
            setCurrentCarouselPageIndex(0)
        },
        [advertisementCategories, setOffersTabActive, setSelectedCategory, setCurrentCarouselPageIndex]
    )

    const titleProps: Pick<SimpleWidgetCardProps, "icon" | "title" | "headerAction"> | undefined = !hideTitle
        ? { title: translateText(1276), icon: "sales", headerAction: hideShowAllButton ? null : <OfferDetailsButton /> }
        : undefined

    const showTabs = hasOfferCarouselModuleEnabled || !!externalSystemTabs?.length

    if ((!hasOfferCarouselModuleEnabled && !hasOffersModuleEnabled && !externalSystemTabs?.length) || isDisabled) {
        return null
    }

    return (
        <LegacyContainer className={classes(offersWidgetClasses.root, className)} size={size ?? "4x4"} sx={sx}>
            <SimpleWidgetCard widgetVariant={variant ?? "startpage"} {...titleProps}>
                <Stack height="100%">
                    {showTabs && (
                        <OffersWidgetTabs value={activeTabIdentifier} onChange={handleTabChange} layout="buttons">
                            {hasOffersModuleEnabled && hasOfferCarouselModuleEnabled && (
                                <Tab value={OFFERS_KEY} label={getTabText(offersModuleDescription)} />
                            )}
                            {hasOfferCarouselModuleEnabled &&
                                (showOfferCarouselAsChild ? (
                                    <Tab value={OFFER_CAROUSEL_KEY} label={getTabText(offerCarouselModuleDescription)} />
                                ) : (
                                    advertisementCategories?.map((cat) => (
                                        <Tab
                                            key={`${OFFER_CAROUSEL_KEY}|${cat.id}`}
                                            value={`${OFFER_CAROUSEL_KEY}|${cat.id}`}
                                            label={cat.description}
                                        />
                                    ))
                                ))}
                            {externalSystemTabs?.map((item) => (
                                <Tab
                                    key={item.text}
                                    value={item.text}
                                    label={getTabText(item.text)}
                                    onClick={() => item.path && openLink(item.path)}
                                    icon={<Icon name="external-link" />}
                                    iconPosition="end"
                                />
                            ))}
                        </OffersWidgetTabs>
                    )}
                    {activeTabIdentifier === OFFERS_KEY && ( // To avoid missmatch of the late rendered tab
                        <OffersWidgetTabPanel sx={{ pt: 1, pb: showOfferCarouselAsChild ? 4 : 0 }}>
                            <Stack flex={1}>
                                <OffersFrame params={iframeParams} />
                            </Stack>
                        </OffersWidgetTabPanel>
                    )}
                    {activeTabIdentifier.startsWith(OFFER_CAROUSEL_KEY) && ( // To avoid missmatch of the late rendered tab
                        <OffersWidgetTabPanel>
                            <OffersCarousel hideAdvertisementCategories={!showOfferCarouselAsChild} />
                        </OffersWidgetTabPanel>
                    )}
                    {showOfferCarouselAsChild && <OfferDetailsButton sx={{ alignSelf: "start" }} />}
                </Stack>
            </SimpleWidgetCard>
        </LegacyContainer>
    )
}

export const OffersWidget = styled(OffersWidgetComponent, {
    name: offersWidgetName,
    slot: offersWidgetSlots.root,
    overridesResolver: (_, styles) => styles.root,
})({})
